import { createFileRoute } from "@tanstack/react-router";
import { Download, DollarSign } from "lucide-react";
import type { ReactNode } from "react";

import { AnimatedThemeToggler } from "@/components/ui/animated-theme-toggler";
import { PageNoise } from "@/features/portfolio/components/page-noise";
import { ProfilePanel } from "@/features/portfolio/components/profile-panel";
import {
  OpenSourceProjectCard,
  PROJECT_ACCENTS,
  SaasProjectCard,
} from "@/features/portfolio/components/project-card";
import { openSourceProjects, profile, saasProjects } from "@/features/portfolio/config";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/(public)/")({
  loader: () => ({ crumb: "Home" }),
  head: () => ({
    meta: [{ title: profile.name }, { name: "description", content: profile.bio }],
  }),
  component: PortfolioPage,
});

function Shelf({
  title,
  measure,
  icon,
  chipClassName,
  children,
}: {
  title: string;
  /** What the charts in this shelf plot, e.g. "monthly revenue". */
  measure: string;
  /** Category glyph shown in the tinted chip: money or downloads. */
  icon: ReactNode;
  chipClassName: string;
  children: ReactNode;
}) {
  return (
    <section>
      <div className="flex items-center justify-between gap-4">
        <h2 className="flex items-center gap-2.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          <span className={cn("grid size-6 place-items-center rounded-full", chipClassName)}>{icon}</span>
          {title}
        </h2>
        <p className="text-xs text-muted-foreground/80">{measure}</p>
      </div>
      <div className="mt-3 grid gap-4 sm:grid-cols-2">{children}</div>
    </section>
  );
}

/**
 * The public landing page: profile rail on the left, project shelves with
 * live numbers on the right. Single column on mobile, rail goes sticky from
 * lg up.
 */
function PortfolioPage() {
  return (
    <main className="relative min-h-screen">
      <PageNoise />
      {/* Whenever the pinned pill shows (phones, short desktop windows), the page
          reserves bottom padding for it; with the in-rail card it keeps normal padding. */}
      <div className="mx-auto w-full max-w-[96rem] px-5 py-10 pb-24 sm:px-8 lg:py-16 lg:pb-24 lg:[@media(min-height:960px)]:pb-16">
        <div className="absolute right-5 top-5 sm:right-8">
          <AnimatedThemeToggler className="text-muted-foreground hover:text-foreground size-9" />
        </div>
        <div className="grid gap-12 lg:grid-cols-[minmax(0,340px)_minmax(0,1fr)] lg:gap-14">
          {/* Sticky only when the whole rail fits the screen; top-16 matches the
              container's lg:py-16, so the pinned position equals the natural one and
              the rail never shifts. On shorter screens the rail flows with the page. */}
          <div className="lg:self-start lg:[@media(min-height:960px)]:sticky lg:[@media(min-height:960px)]:top-16">
            <ProfilePanel />
          </div>

          <div className="flex flex-col gap-10">
            <Shelf title="Products" measure="monthly revenue" icon={<DollarSign className="size-3.5" />} chipClassName={PROJECT_ACCENTS.saas.chip}>
              {saasProjects.map((project) => (
                <SaasProjectCard key={project.id} project={project} />
              ))}
            </Shelf>

            <Shelf title="Open Source" measure="npm downloads per month" icon={<Download className="size-3.5" />} chipClassName={PROJECT_ACCENTS.openSource.chip}>
              {openSourceProjects.map((project) => (
                <OpenSourceProjectCard key={project.id} project={project} />
              ))}
            </Shelf>
          </div>
        </div>
      </div>
    </main>
  );
}
